import { Box, Typography, Avatar, Stack } from "@mui/material";
import theme from "@/theme";
import AmericaFlagSvg from "@/assets/svg/AmericanFlagSvg.svg";
import ArrowSvg from "@/assets/svg/ArrowSvg";
import { useGetCompanyInfoQuery } from "@/store/api/financeApi";
import fallbackImage from "@/assets/image/fallback.png";
import styles from "./stylesCompanyInfoPopup";
import { useGetCompanyLogoQuery } from "@/store/api/trendingStocksApi";
import { useNavigate } from "react-router-dom";
import CustomLoader from "../../CustomLoader/CustomLoader";
interface ProfileHoverCardProps {
  symbol: string | undefined;
}

export default function CompanyInfoPopup({ symbol }: ProfileHoverCardProps) {
  const cleanedSymbol = symbol?.replace(/^\$/, "").toUpperCase() ?? "";
  const { data: logoUrl } = useGetCompanyLogoQuery(cleanedSymbol);
  const navigate = useNavigate();
  const {
    data: companyInfo,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
  } = useGetCompanyInfoQuery(cleanedSymbol);
  const handleClick = (symbol: string) => {
    navigate(`/stock/${symbol}`);
  };
  if (isCompanyLoading) {
    return (
      <Box sx={styles.wrapper} display="flex" flexDirection="column" alignItems="center" gap="12px">
        <CustomLoader size={40} />
        <Typography variant="h6" color={theme.palette.custom.blueGray600}>
          Loading company info...
        </Typography>
      </Box>
    );
  }

  if (!symbol || !companyInfo || isCompanyError) {
    return (
      <Box sx={styles.wrapper} display="flex" flexDirection="column" alignItems="center" gap="12px">
        <Typography variant="h6" color="error">
          Failed to load company data.
        </Typography>
        <Typography variant="body2" color={theme.palette.custom.blueGray500}>
          Please check the symbol or try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={styles.wrapper}
      onClick={() => handleClick(symbol.startsWith("$") ? symbol.slice(1) : symbol)}
    >
      <Box sx={styles.topRow}>
        <Box>
          <Avatar
            src={"https://eodhd.com/" + logoUrl || fallbackImage}
            alt={symbol}
            sx={styles.avatar}
            imgProps={{
              onError: (e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = fallbackImage;
              },
            }}
          />
        </Box>
        <Box>
          <Typography sx={styles.companyName} color={theme.palette.custom.blueGray700}>
            {companyInfo.companyName}
          </Typography>
          <Stack direction="row" sx={styles.symbolRow}>
            <Typography fontSize="h6" color={theme.palette.custom.blueGray600}>
              {companyInfo.symbol}
            </Typography>
            <img src={AmericaFlagSvg} />
            <Typography fontSize="h6" color={theme.palette.custom.blueGray600}>
              {companyInfo.exchange}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box sx={styles.middle}>
        <Stack direction="column">
          <Stack direction="row" sx={styles.priceRow}>
            <Typography
              variant="h2"
              color={theme.palette.custom.blueGray700}
              lineHeight="130%"
              fontWeight={500}
            >
              {companyInfo.price}
            </Typography>
            <Stack direction="row" alignItems="center" gap="4px">
              <ArrowSvg type={companyInfo.change < 0 ? "down" : "up"} width={16} height={16} />
              <Typography
                variant="h5"
                color={companyInfo.change < 0 ? "#ff6a55" : "#53c854"}
                lineHeight="130%"
                fontWeight={500}
              >
                {companyInfo.change.toFixed(2)} ({companyInfo.changePercent.toFixed(2)}%)
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <Box sx={styles.bottom}>
        <Box sx={styles.bottomInner}>
          <Box
            sx={{
              ...styles.redDot,
              bgcolor:
                companyInfo.marketStatus === "closed"
                  ? theme.palette.custom.secondaryRed
                  : theme.palette.success.main,
            }}
          />
          <Typography fontWeight={500} color={theme.palette.custom.blueGray700} variant="h6">
            Market {companyInfo.marketStatus === "closed" ? "Closed" : "Open"}
          </Typography>
          <Typography color={theme.palette.custom.blueGray600}>
            {companyInfo.marketTime ? `(as of ${companyInfo.marketTime})` : null}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
